import { useParams } from "react-router-dom";
import { useBundle, bundleViewState } from "../../lib/bundle";
import { formatDateRange, registrationOpen, venueMapsHref } from "../../lib/format";
import { registerTeamPath } from "../../lib/routes";
import { trackRegisterStart } from "../../lib/analytics";
import { TournamentBanner } from "../../components/TournamentBanner";
import {
  ArrowUpRightIcon,
  Badge,
  ButtonLink,
  CalendarIcon,
  Card,
  EmptyState,
  LoadingBlock,
  MapPinIcon,
  Section,
  TournamentStatusBadge,
  stagger,
} from "../../components/ui";

/** Public tournament home (spec §10): dates, venues, divisions, register CTA. */
export function TournamentHome() {
  const { tid } = useParams();
  const q = useBundle(tid);
  const bundle = q.data;

  const view = bundleViewState(q);
  if (view === "notFound")
    return <EmptyState title="Tournament not found" className="mx-auto max-w-md" />;
  if (view === "error")
    return (
      <EmptyState
        title="Couldn't load this tournament"
        hint="Check your connection and try again."
        className="mx-auto max-w-md"
      />
    );
  if (view === "loading" || !bundle) return <LoadingBlock />;
  const t = bundle.tournament;
  const windowOpen = registrationOpen(t);
  const divisions = [...bundle.divisions].sort((a, b) => a.order - b.order);

  return (
    /*
      Still an editorial column rather than the full 1536px shell — a banner, a
      blurb, and two short card lists do not become better that wide. But 6xl
      is wide enough to seat the divisions beside the reading instead of below
      it, which is the point of the split further down.
    */
    <div className="max-w-6xl space-y-8 sm:space-y-10">
      <TournamentBanner name={t.name} seed={t.id} src={t.bannerImageUrl} size="hero" />
      <div className="animate-rise space-y-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <CalendarIcon className="h-4 w-4 text-slate-400" />
            {formatDateRange(t.startDate, t.endDate)}
          </span>
          {t.location ? (
            <span className="inline-flex items-center gap-1.5">
              <MapPinIcon className="h-4 w-4 text-slate-400" />
              {t.location}
            </span>
          ) : null}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-navy-950 sm:text-4xl">
          {t.name}
        </h1>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {windowOpen ? (
            <>
              <ButtonLink to={registerTeamPath(t.id)} onClick={trackRegisterStart}>
                Register a team
              </ButtonLink>
              <Badge tone="green" pulse>
                Registration open
              </Badge>
            </>
          ) : t.registration ? (
            <Badge tone="slate">Registration closed</Badge>
          ) : null}
          {/*
            Deliberately a denylist, unlike the visibility gates in
            packages/shared/src/schemas.ts (which are an allowlist on purpose).
            This is only a cosmetic label, so it should fail OPEN: a future
            status we haven't special-cased still gets a badge instead of
            silently vanishing. The visibility gates protect data, so those
            must fail CLOSED instead.
          */}
          {t.status !== "published" ? <TournamentStatusBadge status={t.status} /> : null}
        </div>
      </div>

      {/*
        Divisions is the only thing on this page you can act on — schedule,
        standings, and bracket are all reached from here, and everything else
        is prose. So it leads the DOM: on a phone it sits at the top, because
        scrolling past a description to reach the link you came for is the
        whole cost of stacking.

        At lg it becomes the left rail, which is also where the DOM already
        puts it, so reading order and visual order agree. A screen reader
        therefore hears the divisions before the About text — the inverse of
        `Workbench`, and the right way round here: that list is the navigation,
        not a form appended to a collection. The headline sits above the rail
        rather than above the prose, so the About text starts a rail-width in
        from the page's left edge.
      */}
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(18rem,22rem)_minmax(0,1fr)]">
        <Section title="Divisions" className="min-w-0 animate-rise" style={stagger(2)}>
          {divisions.length === 0 ? (
            <EmptyState
              title="Divisions coming soon"
              hint="The organizer hasn't posted divisions yet."
            />
          ) : (
            /* One column at every width now: the rail has room for exactly one. */
            <div className="grid gap-3">
              {divisions.map((d) => (
                /*
                  `lg:` undoing `sm:` is deliberate. Below lg this card is as
                  wide as the page and its name sits beside its links; at lg it
                  moves into a 22rem rail, where a viewport-width breakpoint no
                  longer says anything about the space the card actually has,
                  so it stacks again.
                */
                <Card
                  key={d.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between lg:flex-col lg:items-stretch"
                >
                  <span className="font-display font-semibold text-navy-950">{d.name}</span>
                  <div className="grid grid-cols-3 gap-2 sm:flex lg:grid">
                    {(["schedule", "standings", "bracket"] as const).map((page) => (
                      <ButtonLink
                        key={page}
                        variant="secondary"
                        size="sm"
                        to={`/t/${t.id}/d/${d.id}/${page}`}
                        className="capitalize"
                      >
                        {page}
                      </ButtonLink>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Section>

        <div className="min-w-0 space-y-8 sm:space-y-10">
          {t.description ? (
            <Section title="About" className="animate-rise" style={stagger(3)}>
              <Card className="p-5 sm:p-6">
                <p className="max-w-prose whitespace-pre-line leading-relaxed text-slate-600">
                  {t.description}
                </p>
              </Card>
            </Section>
          ) : null}

          {bundle.venues.length > 0 ? (
            <Section title="Where you'll play" className="animate-rise" style={stagger(4)}>
              {/*
                Two columns, not three: the reading column tops out near 768px
                once the rail takes its share, and a third venue card there is
                narrower than the address inside it.
              */}
              <div className="grid gap-3 sm:grid-cols-2">
                {bundle.venues.map((v) => (
                  <Card key={v.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brandText">
                        <MapPinIcon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <a
                          href={venueMapsHref(v)}
                          target="_blank"
                          rel="noreferrer"
                          className="group inline-flex items-center gap-1 rounded font-medium text-navy-950 transition-colors hover:text-brandText focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                        >
                          {v.name}
                          <ArrowUpRightIcon className="h-3.5 w-3.5 text-slate-300 transition-all duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-600" />
                        </a>
                        <p className="text-sm text-slate-500">{v.address}</p>
                      </div>
                    </div>
                    {v.fields.length > 0 ? (
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          {v.fields.length === 1 ? "Field" : "Fields"}
                        </p>
                        <ul className="flex flex-wrap gap-1.5">
                          {v.fields.map((f, i) => (
                            <li
                              key={i}
                              className="inline-flex items-baseline gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                            >
                              {f.name}
                              {f.notes ? <span className="text-slate-400">{f.notes}</span> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </Card>
                ))}
              </div>
            </Section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
